import { useSelector } from 'react-redux';
import { selectClient } from '@/model';
import {
  BenefitDesignQuoteCard,
  TraditionalLTCQuoteCard,
  NoQuotesAvailable,
  QuoteErrorDisplay,
  getLtcTiming,
  useExpandedQuoteIds,
  type BenefitDesignQuoteGroup,
  type TraditionalLTCQuoteWithROI,
  type PaymentOption,
} from '@/features/shop-insurance';
import { VALIDATION_ERROR_TYPES } from '@client/util/quoteTypeGuards';

interface BenefitDesignQuotesListProps {
  quoteGroups: BenefitDesignQuoteGroup[];
  filteredTraditionalLTCQuotesWithEligibility: TraditionalLTCQuoteWithROI[];
  selectedTab: ShopInsuranceTab;
  clientPartner: any;
  paymentOption: PaymentOption;
  advisorId: string;
  isQuoteSelected: (quote: any) => { isSelected: boolean; fundingSourceId: string | null };
  onClose: () => void;
  knownLimitationError?: QuoteError | null;
}

export function BenefitDesignQuotesList({
  quoteGroups,
  filteredTraditionalLTCQuotesWithEligibility,
  selectedTab,
  clientPartner,
  paymentOption,
  advisorId,
  isQuoteSelected,
  onClose,
  knownLimitationError,
}: BenefitDesignQuotesListProps) {
  const client = useSelector(selectClient);
  const { careStartAge, yearsUntilLtc } = getLtcTiming(
    client?.intakeSurvey?.clientAge,
    client?.appliedInferenceSet?.ltcAtAge,
  );
  const { expandedIds, toggleExpandedId } = useExpandedQuoteIds();

  if (knownLimitationError && VALIDATION_ERROR_TYPES.includes(knownLimitationError.type)) {
    return <QuoteErrorDisplay error={knownLimitationError} />;
  }

  const hasAnyQuotes =
    quoteGroups.length > 0 || filteredTraditionalLTCQuotesWithEligibility.length > 0;

  if (!hasAnyQuotes) {
    return (
      <NoQuotesAvailable
        advisorId={advisorId}
        reason={knownLimitationError?.type}
      />
    );
  }

  return (
    <div
      className="flex w-full flex-col gap-4"
      data-testid="benefit-design-quotes-list"
    >
      {quoteGroups.map(group => {
        const id = group.representativeQuote.insuranceQuoteId;

        return (
          <BenefitDesignQuoteCard
            key={id}
            testId="benefit-design-quote-card"
            group={group}
            onClose={onClose}
            selectedTab={selectedTab}
            clientPartner={clientPartner}
            isQuoteSelected={isQuoteSelected}
            careStartAge={careStartAge}
            yearsUntilLtc={yearsUntilLtc}
          />
        );
      })}

      {filteredTraditionalLTCQuotesWithEligibility.map(quote => {
        const id = quote.traditionalLTCQuoteId;
        const { isSelected, fundingSourceId } = isQuoteSelected(quote);

        return (
          <TraditionalLTCQuoteCard
            key={id}
            testId="benefit-design-traditional-quote-card"
            quote={quote}
            isExpanded={expandedIds.has(id)}
            onToggleExpand={() => toggleExpandedId(id)}
            onClose={onClose}
            isSelected={isSelected}
            fundingSourceId={fundingSourceId}
          />
        );
      })}
    </div>
  );
}
